import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Carrier, Site } from '@klareospace/pharos-lib-types';

import { useEnableOnSite } from './use-enable-on-site';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UseEnableSiteFormReturn = {
  from: Date | null | undefined;
  isPending: boolean;
  onSubmit: () => void;
  setFrom: (date: Date | null | undefined) => void;
  setTo: (date: Date | null | undefined) => void;
  to: Date | null | undefined;
  validationError: string | null;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEnableSiteForm(
  carrier: Carrier | undefined,
  site: Site | undefined,
  spaceId: string,
  onClose: () => void,
): UseEnableSiteFormReturn {
  const { t } = useTranslation('settings');
  const enableOnSite = useEnableOnSite();
  const [from, setFrom] = useState<Date | null | undefined>(undefined);
  const [to, setTo] = useState<Date | null | undefined>(undefined);

  // Reset state when carrier or site changes
  useEffect(() => {
    setFrom(undefined);
    setTo(undefined);
  }, [carrier, site]);

  const validationError = useMemo<string | null>(() => {
    if (from && to && new Date(from) >= new Date(to)) {
      return t('sites.settings.carriers.validation.startBeforeEnd');
    }
    return null;
  }, [from, t, to]);

  function onSubmit(): void {
    if (!carrier || !site || validationError) return;
    enableOnSite
      .mutateAsync({
        carrierId: carrier.id,
        from,
        siteId: site.id,
        spaceId,
        to,
      })
      .then(
        () => onClose(),
        () => {
          // error handled in hook via toast
        },
      );
  }

  return { from, isPending: enableOnSite.isPending, onSubmit, setFrom, setTo, to, validationError };
}
